import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { EventResponse, TrainingDayCapacityResponse } from '../../../../../../../../@shared/models/response.module';
import { TrainingService } from '../../../../../../../../@shared/services/training.service';

interface DayCapacityForm {
  training_date: string;
  dayLabel: string;
  max_group_sessions: number;
  max_individual_sessions: number;
  group_session_capacity: number;
  group_session_duration: number;
  individual_session_duration: number;
  capacity_id?: number;
  saved: boolean;
}

@Component({
  selector: 'app-wizard-step-capacity',
  standalone: false,
  templateUrl: './wizard-step-capacity.html',
  styleUrl: './wizard-step-capacity.css'
})
export class WizardStepCapacity implements OnInit, OnChanges {
  @Input() event: EventResponse | null = null;
  @Output() capacityChanged = new EventEmitter<number>();

  days: DayCapacityForm[] = [];
  loading = false;
  saving = false;

  constructor(private trainingService: TrainingService) {}

  ngOnInit() {
    this.initializeDays();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['event'] && this.event) {
      this.initializeDays();
    }
  }

  initializeDays() {
    if (!this.event?.start_date || !this.event?.end_date) return;

    // Parse dates - handle both date strings and datetime strings
    const startDate = this.parseDate(this.event.start_date);
    const endDate = this.parseDate(this.event.end_date);

    if (!startDate || !endDate) return;

    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

    this.days = [];
    const currentDate = new Date(startDate);

    while (currentDate <= endDate) {
      // Format date as YYYY-MM-DD without timezone conversion
      const year = currentDate.getFullYear();
      const month = String(currentDate.getMonth() + 1).padStart(2, '0');
      const day = String(currentDate.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;

      const dayName = dayNames[currentDate.getDay()];
      const dayNum = currentDate.getDate();
      const monthName = currentDate.toLocaleString('default', { month: 'short' });

      this.days.push({
        training_date: dateStr,
        dayLabel: `${dayName}, ${monthName} ${dayNum}`,
        max_group_sessions: 0,
        max_individual_sessions: 0,
        group_session_capacity: 6,
        group_session_duration: 60,
        individual_session_duration: 45,
        saved: false
      });

      currentDate.setDate(currentDate.getDate() + 1);
    }

    // Load existing capacity data
    this.loadCapacity();
  }

  parseDate(dateValue: string | Date | null | undefined): Date | null {
    if (!dateValue) return null;

    // If it's already a Date object
    if (dateValue instanceof Date) {
      return new Date(dateValue.getFullYear(), dateValue.getMonth(), dateValue.getDate());
    }

    // If it's a string, extract just the date part
    const dateStr = String(dateValue);

    // Handle US format: "01/03/2026, 09:58 AM" or "01/03/2026"
    if (dateStr.includes('/')) {
      const datePart = dateStr.split(',')[0].trim(); // Remove time part
      const parts = datePart.split('/');
      if (parts.length === 3) {
        const month = +parts[0];
        const day = +parts[1];
        const year = +parts[2];
        return new Date(year, month - 1, day);
      }
    }

    // Handle ISO format: "2026-01-03T08:00:00" or "2026-01-03"
    // Also handle space separator: "2026-01-03 08:00:00"
    const datePart = dateStr.split('T')[0].split(' ')[0];
    const parts = datePart.split('-');

    if (parts.length === 3) {
      return new Date(+parts[0], +parts[1] - 1, +parts[2]);
    }

    // Fallback: try native parsing
    const parsed = new Date(dateStr);
    if (!isNaN(parsed.getTime())) {
      return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
    }

    return null;
  }

  normalizeDateToISO(dateValue: string | null | undefined): string | null {
    if (!dateValue) return null;

    const dateStr = String(dateValue);

    // Already in ISO format: "2026-01-03" or "2026-01-03T..."
    if (dateStr.match(/^\d{4}-\d{2}-\d{2}/)) {
      return dateStr.split('T')[0];
    }

    // US format: "01/03/2026, 09:58 AM" or "01/03/2026"
    if (dateStr.includes('/')) {
      const datePart = dateStr.split(',')[0].trim();
      const parts = datePart.split('/');
      if (parts.length === 3) {
        const month = parts[0].padStart(2, '0');
        const day = parts[1].padStart(2, '0');
        const year = parts[2];
        return `${year}-${month}-${day}`;
      }
    }

    return null;
  }

  loadCapacity() {
    if (!this.event) return;

    this.loading = true;
    this.trainingService.getDayCapacity({ event_id: this.event.event_id }).subscribe({
      next: (capacities) => {
        // Merge with existing days - normalize dates for comparison
        for (const cap of capacities) {
          const capDateNormalized = this.normalizeDateToISO(cap.training_date);
          const day = this.days.find(d => d.training_date === capDateNormalized);
          if (day) {
            day.capacity_id = cap.capacity_id;
            day.max_group_sessions = cap.max_group_sessions;
            day.max_individual_sessions = cap.max_individual_sessions;
            day.group_session_capacity = cap.group_session_capacity;
            day.group_session_duration = cap.group_session_duration;
            day.individual_session_duration = cap.individual_session_duration;
            day.saved = true;
          }
        }
        this.emitCapacityCount();
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      }
    });
  }

  saveDay(day: DayCapacityForm) {
    if (!this.event) return;

    this.saving = true;
    this.trainingService.setDayCapacity({
      event_id: this.event.event_id,
      training_date: day.training_date,
      max_group_sessions: day.max_group_sessions,
      max_individual_sessions: day.max_individual_sessions,
      group_session_capacity: day.group_session_capacity,
      group_session_duration: day.group_session_duration,
      individual_session_duration: day.individual_session_duration
    }).subscribe({
      next: (result) => {
        day.capacity_id = result.capacity_id;
        day.saved = true;
        this.emitCapacityCount();
        this.saving = false;
      },
      error: () => {
        this.saving = false;
      }
    });
  }

  saveAllDays() {
    const unsavedDays = this.days.filter(d =>
      !d.saved && (d.max_group_sessions > 0 || d.max_individual_sessions > 0)
    );

    for (const day of unsavedDays) {
      this.saveDay(day);
    }
  }

  onDayChange(day: DayCapacityForm) {
    day.saved = false;
  }

  emitCapacityCount() {
    const configuredDays = this.days.filter(d =>
      d.max_group_sessions > 0 || d.max_individual_sessions > 0
    ).length;
    this.capacityChanged.emit(configuredDays);
  }

  getTotalGroupSlots(day: DayCapacityForm): number {
    return day.max_group_sessions * day.group_session_capacity;
  }

  getTotalSlots(): { group: number; individual: number; totalSessions: number } {
    return this.days.reduce((acc, day) => ({
      group: acc.group + day.group_session_capacity,
      individual: acc.individual + day.max_individual_sessions,
      totalSessions: acc.totalSessions + this.getTotalGroupSlots(day)
    }), { group: 0, individual: 0, totalSessions: 0 });
  }

  hasAnyCapacity(): boolean {
    return this.days.some(d => d.max_group_sessions > 0 || d.max_individual_sessions > 0);
  }

  hasUnsavedChanges(): boolean {
    return this.days.some(d =>
      !d.saved && (d.max_group_sessions > 0 || d.max_individual_sessions > 0)
    );
  }
}
